<template>
  <div class="outer">
    <div
      class="scope-item"
      :class="{
        selected,
        decorative,
        matched: isMatch,
        unmatched: isUnmatch,
        childmatched: isChildMatch,
        childselected: isChildSelected,
      }"
      @click="clicked"
    >
      <flex-row gap vcenter>
        <flex-cell class="select-mark" shrink v-if="!decorative">
          <ux-icon :icon="selected ? 'fa-check' : 'fa-regular fa-circle'" />
        </flex-cell>
        <flex-cell shrink>
          <ux-circle :value="item" />
        </flex-cell>
        <flex-cell class="scope-title">
          {{ item.title }}
        </flex-cell>
        <flex-cell class="definition-title" v-if="definition" shrink>
          {{ definition.title }}
        </flex-cell>
        <flex-cell shrink v-if="hasChildren">
          <!-- <div :class="{hide:!hasChildren}"> -->
          <ux-button @click.stop.prevent="toggleCollapse()" size="xs" icon>
            <ux-icon :icon="expanded ? 'fa-angle-up' : 'fa-angle-down'" />
          </ux-button>
          <!-- </div> -->
        </flex-cell>
      </flex-row>
    </div>
    <div class="children" v-if="expanded">
      <scope-item
        :click="click"
        :item="child"
        :keywords="keywords"
        v-for="child in item.children"
      />
    </div>
  </div>
</template>
<script>
import UxCircle from "../ui/circle.vue";

function matchesKeywords(keywords, title) {
  keywords = String(keywords || "").toLowerCase();
  title = String(title || "").toLowerCase();
  return title.includes(keywords);
}

export default {
  components: {
    UxCircle,
  },
  props: {
    item: {
      type: Object,
    },
    click: {
      type: Function,
    },
    keywords: {
      type: String,
      default: "",
    },
  },
  data() {
    return {};
  },
  methods: {
    clicked() {
      if (this.decorative) {
        // Context-only row — not selectable; toggle its branch instead.
        if (this.hasChildren) {
          this.toggleCollapse();
        }
        return;
      }
      this.click(this.item);
    },
    toggleCollapse() {
      return (this.collapsed = !this.collapsed);
    },
  },
  created() {
    this.$glossary.ensure();
  },
  inject: {
    registry: {},
    isSelected: {
      default() {
        return;
      },
    },
    scopeInclude: {
      default: "scope",
    },
  },
  computed: {
    glossary() {
      return this.$glossary.hash;
    },
    definition() {
      const self = this;
      const definitionKey = this.item?.meta?.definition;

      if (!definitionKey) {
        return;
      }

      return this.glossary[definitionKey];
    },
    selected() {
      return this.isSelected(this.item);
    },
    decorative() {
      // In unit-only pickers, non-unit scopes are context only — not selectable.
      return this.scopeInclude === "unit" && !this.item.unit;
    },
    isChildMatch() {
      const self = this;

      function checkChildren(children) {
        if (!children || children.length === 0) {
          return false;
        }

        let hasMatch = false;

        children.forEach(function (child) {
          if (hasMatch) return; // Skip if we already found a match

          if (matchesKeywords(self.keywords, child.title)) {
            hasMatch = true;
            return;
          }

          if (self.isSelected(child)) {
            hasMatch = true;
            return;
          }

          if (child.children && checkChildren(child.children)) {
            hasMatch = true;
          }
        });

        return hasMatch;
      }

      return checkChildren(self.item.children);
    },
    isMatching() {
      return !!this.keywords?.length;
    },
    isMatch() {
      if (!this.isMatching) {
        return false;
      }

      return matchesKeywords(this.keywords, this.item.title);
    },
    isUnmatch() {
      if (!this.isMatching) {
        return;
      }
      return !this.isMatch;
    },
    hasChildren() {
      return this.item.children?.length;
    },
    expanded() {
      return !this.collapsed;
    },
    collapsed: {
      set(b) {
        this.registry[this.item._id] = this.item.collapsed = b;
      },
      get() {
        if (!this.hasChildren) {
          return true;
        }

        if (this.keywords) {
          return false;
        }

        if (this.registry[this.item._id]) {
          return true;
        }

        return this.item.collapsed;
      },
    },
  },
};
</script>
<style lang="scss" scoped>
.outer {
}

.scope-item {
  padding: 0.6em 1em;
  background: var(--bg1, #fff);
  border: 1px solid color-mix(in lab, currentColor 20%, transparent);
  display: block;
  margin-bottom: 0.1em;
  border-radius: 0.3em;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
    color: color-mix(in lab, currentColor 80%, transparent);
  }

  .select-mark {
    transition: all 0.2s;
    transform: scale(0.5);
    opacity: 0.3;
  }

  &.selected {
    opacity: 1;
    .select-mark {
      transform: scale(1);
      opacity: 0.8;
    }
  }

  /* &.unmatched {
    & > .item {
      opacity: 0.3;
    }
  } */

  &.unmatched:not(.childmatched, .selected) {
    display: none;
  }

  &.decorative {
    background: transparent;
    border-color: transparent;
    opacity: 0.55;
    font-size: 0.8em;
    padding: 0.3em 1em;
    cursor: default;
  }
}

.hide {
  visibility: hidden;
}

.children {
  position: relative;
  padding-left: 1em;
  margin-left: 1em;
  padding-right: 0.3em;

  .scope-item {
    position: relative;

    &:after {
      content: "";
      display: block;
      width: 1em;
      left: -1em;
      top: 1.6em;
      position: absolute;
      border-top: 1px dotted color-mix(in lab, currentColor 20%, transparent);
    }
  }

  &:after {
    content: "";
    display: block;
    width: 1px;
    height: calc(100% - 0.9em);
    top: 0;
    left: 0;
    position: absolute;
    border-left: 1px dotted color-mix(in lab, currentColor 20%, transparent);
  }
}

.definition-title {
  opacity: 0.5;
  font-size: 0.8em;
}
</style>
